import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DialogClose } from '@/components/DialogClose';
import {
  useRunScheduleNow, useSaveSchedule,
  type ScheduleInput, type SyncSchedule, type SyncSchedules,
} from '@/api/hrms-sync';
import { errorMessage } from '@/lib/api-client';
import { fmtWhen } from './format';

/**
 * The nightly uKnowva sync for this workspace: when it runs (local time and
 * timezone), how many days back each run re-reads, and which data it brings.
 * Punches corrected in uKnowva a day or two later are picked up by the
 * look-back; a night the server was down is made up the next night.
 */
export function SyncScheduleDialog({
  open, data, schedule, canEdit, onClose, onStarted,
}: {
  open: boolean;
  data: SyncSchedules;
  schedule: SyncSchedule;
  canEdit: boolean;
  onClose: () => void;
  onStarted?: (runId: string) => void;
}) {
  const save = useSaveSchedule();
  const runNow = useRunScheduleNow();
  const [form, setForm] = useState<ScheduleInput>(schedule);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  // Only when the dialog opens: the refetch that follows a Save hands in a new
  // `schedule` and must not wipe the "Saved" note (the form already matches it).
  useEffect(() => {
    if (!open) return;
    setForm({
      is_enabled: schedule.is_enabled, run_at: schedule.run_at, timezone: schedule.timezone,
      lookback_days: schedule.lookback_days, include_today: schedule.include_today,
      sources: schedule.sources,
    });
    setError(null); setSaved(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Everything uKnowva offers now, plus anything this schedule still names that
  // it no longer does — kept visible (and ticked) so it can be taken off.
  const available = useMemo(() => {
    const named = new Set([...schedule.sources, ...form.sources]);
    return data.sources.filter((s) => s.is_available || named.has(s.source_key));
  }, [data.sources, schedule.sources, form.sources]);
  const set = <K extends keyof ScheduleInput>(k: K, value: ScheduleInput[K]) =>
    setForm((f) => ({ ...f, [k]: value }));
  const toggleSource = (key: string) => set('sources',
    form.sources.includes(key) ? form.sources.filter((s) => s !== key) : [...form.sources, key]);

  const onSave = async () => {
    setError(null); setSaved(null);
    try {
      await save.mutateAsync({ name: schedule.name, data: form });
      setSaved(form.is_enabled ? 'Saved. The sync runs every day at this time.' : 'Saved. The daily sync is off.');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const onRunNow = async () => {
    setError(null); setSaved(null);
    try {
      const res = await runNow.mutateAsync(schedule.name);
      onStarted?.(res.run_id);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {schedule.label}
        <DialogClose onClose={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">{schedule.description}</Typography>
          {!data.scheduler.in_app && (
            <Alert severity="info">
              The in-app scheduler is off on this server; an external cron runs the due schedules
              (<code>python -m app.tenancy.cli sync-schedules --all</code>). These settings still decide
              when and what.
            </Alert>
          )}
          <FormControlLabel
            control={<Switch checked={form.is_enabled} disabled={!canEdit}
              onChange={(_, v) => set('is_enabled', v)} />}
            label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Run every day</Typography>}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField type="time" label="At" size="small" sx={{ width: 150 }} disabled={!canEdit}
              InputLabelProps={{ shrink: true }} value={form.run_at}
              onChange={(e) => set('run_at', e.target.value)} />
            <Autocomplete size="small" sx={{ minWidth: 260 }} disabled={!canEdit}
              options={data.timezones} value={form.timezone} disableClearable
              onChange={(_, v) => v && set('timezone', v)}
              renderInput={(p) => <TextField {...p} label="Timezone" />} />
            <TextField type="number" label="Days to re-read" size="small" sx={{ width: 160 }}
              disabled={!canEdit} inputProps={{ min: 1, max: 62 }} value={form.lookback_days}
              onChange={(e) => set('lookback_days', Math.max(1, Math.min(62, Number(e.target.value) || 1)))}
              helperText="Picks up late corrections" />
            <FormControlLabel
              control={<Switch checked={form.include_today} disabled={!canEdit}
                onChange={(_, v) => set('include_today', v)} />}
              label="Include today (partial)" />
          </Stack>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Data to bring</Typography>
            <Typography variant="caption" color="text.secondary">
              Employees keep new joiners' days from being skipped; attendance brings the day status,
              punches and overtime. Masters (shifts, leave types…) can be added too.
            </Typography>
            <List dense sx={{ maxHeight: 260, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, mt: 0.5 }}>
              {available.map((s) => (
                <ListItem key={s.source_key} disablePadding
                  onClick={() => canEdit && toggleSource(s.source_key)} sx={{ cursor: canEdit ? 'pointer' : 'default', px: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox size="small" edge="start" checked={form.sources.includes(s.source_key)}
                      disabled={!canEdit} tabIndex={-1} />
                  </ListItemIcon>
                  <ListItemText
                    primary={s.title}
                    secondary={!s.is_available ? 'No longer offered by uKnowva. Skipped by the run'
                      : `${s.source_type}${s.target_kind ? ` → ${s.target_kind}` : ''}${
                        s.target_kind && !s.mapping_confirmed ? ' (mapping not confirmed: stored only)' : ''}`} />
                </ListItem>
              ))}
              {!available.length && (
                <ListItem>
                  <ListItemText secondary="uKnowva's data list hasn't been read yet. Open Sync new data once." />
                </ListItem>
              )}
            </List>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {schedule.next_run_at && (
              <Chip size="small" color="info" variant="outlined"
                label={`Next run ${fmtWhen(schedule.next_run_at)} · attendance ${schedule.next_window.start} → ${schedule.next_window.end}`} />
            )}
            {schedule.last_run && (
              <Chip size="small" variant="outlined"
                label={`Last run ${fmtWhen(schedule.last_run.finished_at || schedule.last_run.started_at)} · ${schedule.last_run.status}`} />
            )}
          </Stack>
          {schedule.last_error && <Alert severity="warning">{schedule.last_error}</Alert>}
          {saved && <Alert severity="success">{saved}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {canEdit && (
          <Button onClick={onRunNow} disabled={runNow.isPending || !form.sources.length} sx={{ mr: 'auto' }}>
            {runNow.isPending ? 'Starting…' : 'Run now'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
        {canEdit && (
          <Button variant="contained" onClick={onSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
